<template>
  <div>
    <base-button block type="info" class="m-0" @click="modal = true">
      <i class="tim-icons icon-pencil pr-2"/>{{ $t('seasonTickets.edit.btnLabel') }}
    </base-button>

    <modal id="CreateDistributor"
           :show.sync="modal"
           modal-classes="modal-dialog-centered modal-lg"
           showClose @close="resetForm()">
      <template slot="header">
        <h4 class="modal-title">
          {{ $t('seasonTickets.edit.titleText') }}
        </h4>
      </template>
      <template>
        <div class="text-center text-muted mb-4">
          <small>{{ $t('seasonTickets.edit.helpText') }}</small>
        </div>
        <base-alert v-if="alert" dismissible :dismiss-callback="() => alert=false" type="danger">
          <!-- TODO: add error translation -->
          {{ getActionErrorDetails }}
        </base-alert>
        <form role="form" @submit.prevent="validate()">
          <div class="row">
            <label class="col-sm-3 col-form-label">{{ $t('seasonTickets.details.duration') }}</label>
            <div class="col-sm-9">
              <base-input
                type="number"
                name="duration"
                v-validate="modelValidations.number"
                :error="getError('duration')"
                v-model="formDetails.duration">
              </base-input>
            </div>
          </div>
          <div class="row">
            <label class="col-sm-3 col-form-label">{{ $t('seasonTickets.details.internal_name') }}</label>
            <div class="col-sm-9">
              <base-input
                type="string"
                name="internal_name"
                v-validate="modelValidations.required"
                :error="getError('internal_name')"
                v-model="formDetails.internal_name">
              </base-input>
            </div>
          </div>
          <div class="row">
            <label class="col-sm-3 col-form-label">{{ $t('seasonTickets.details.internal_description') }}</label>
            <div class="col-sm-9">
              <base-input
                type="string"
                name="internal_description"
                v-validate="modelValidations.required"
                :error="getError('internal_description')"
                v-model="formDetails.internal_description">
              </base-input>
            </div>
          </div>
          <div class="row">
            <label class="col-sm-3 col-form-label">{{ $t('seasonTickets.details.discount_id') }}</label>
            <div class="col-sm-9">
              <base-input
                :error="getError('discount_id')">
                <el-select
                  name="discount_id"
                  data-vv-as="'discount_id'"
                  class="select-primary"
                  v-validate="modelValidations.required"
                  v-model="formDetails.discount_id"
                  :disabled="currentDetails.revision > 1">
                  <el-option
                    v-for="option in discounts"
                    class="select-primary"
                    :value="option.id"
                    :label="option.id"
                    :key="option.id">
                  </el-option>
                </el-select>
              </base-input>
            </div>
          </div>
          <div class="row">
            <label class="col-sm-3 col-form-label">{{ $t('seasonTickets.details.fare_group_id') }}</label>
            <div class="col-sm-9">
              <base-input
                :error="getError('fare_group_id')">
                <el-select
                  name="fare_group_id"
                  data-vv-as="'fare_group_id'"
                  class="select-primary"
                  v-validate="modelValidations.required"
                  v-model="formDetails.fare_group_id">
                  <el-option
                    v-for="option in fareGroups"
                    class="select-primary"
                    :value="option.id"
                    :label="option.description"
                    :key="option.id">
                  </el-option>
                </el-select>
              </base-input>
            </div>
          </div>
          <div class="row">
            <label class="col-sm-3 col-form-label">{{ $t('seasonTickets.details.trait') }}</label>
            <div class="col-sm-9">
              <base-input
                :error="getError('trait')">
                <el-select
                  name="trait"
                  data-vv-as="'trait'"
                  class="select-primary"
                  v-validate="modelValidations.required"
                  v-model="formDetails.trait">
                  <el-option
                    v-for="option in availableTraits"
                    class="select-primary"
                    :value="option"
                    :label="option"
                    :key="option">
                  </el-option>
                </el-select>
              </base-input>
            </div>
          </div>
          <div class="text-center">
            <base-button
              type="info"
              class="my-4"
              @click="validate()"
              :loading="updating"
            >{{ $t('seasonTickets.edit.actionBtnLabel') }}
            </base-button>
          </div>
        </form>
      </template>
    </modal>
  </div>
</template>


<script>
import {BaseAlert, Modal, TagsInput} from 'src/components';
import {DatePicker, Option, Select} from 'element-ui';
import {mapActions, mapState} from "vuex";

export default {
  name: 'editSeasonTicket',
  components: {
    [Option.name]: Option,
    [Select.name]: Select,
    [DatePicker.name]: DatePicker,
    Modal,
    BaseAlert,
    TagsInput
  },
  props: {
    enabled: {
      type: Boolean,
      required: false,
    },
    currentDetails: {
      type: Object,
      required: false,
    },
  },
  data() {
    return {
      modal: false,
      alert: false,
      formDetails: {
        internal_name: this.currentDetails ? this.currentDetails.internal_name : '',
        internal_description: this.currentDetails ? this.currentDetails.internal_description : '',
        discount_id: this.currentDetails ? this.currentDetails.discount_id : '',
        fare_group_id: this.currentDetails ? this.currentDetails.fare_group_id : '',
        duration: this.currentDetails ? this.currentDetails.duration : 1,
        trait: this.currentDetails.trait ? this.currentDetails.trait : 'none',
      },
      modelValidations: {
        required: {
          required: true
        },
        number: {
          numeric: true
        },
      },
      pickerOptions: {
        disabledDate(time) {
          return time.getTime() < new Date((new Date()).getFullYear(), (new Date()).getMonth(), (new Date()).getDate());
        },
      }
    }
  },
  computed: {
    ...mapState({
      loading: state => state.season_tickets.loading,
      updating: state => state.season_tickets.updating,
      error: state => state.season_tickets.error,
      availableTraits: state => state.season_tickets.availableTraits,
      placeOfPurchaseOptions: state => state.season_tickets.placeOfPurchaseOptions,
      discounts: state => state.discount.data,
      fareGroups: state => state.fare_groups.data,
    }),
    getActionErrorDetails() {
      return this.error ? this.error.bodyText : null;
    },
  }, methods: {
    ...mapActions(['LoadDiscounts', 'LoadFareGroups', 'UpdateSeasonTicket']),
    getError(fieldName) {
      return this.errors.first(fieldName);
    },
    validate() {
      this.$validator.validateAll().then(isValid => {
        if (isValid) {
          this.update()
        }
      });
    },
    update: function () {
      let payload = {
        id: this.currentDetails ? this.currentDetails.id : null,
        formSubmit: true,
        data: {
          internal_name: this.formDetails.internal_name,
          internal_description: this.formDetails.internal_description,
          discount_id: this.formDetails.discount_id,
          fare_group_id: this.formDetails.fare_group_id,
          duration: this.formDetails.duration * 1,
          duration_unit: 'day(s)',
          trait: this.formDetails.trait,
        }
      }
      this.UpdateSeasonTicket(payload).then(() => {
        if (this.error != null) {
          this.alert = true
          // got error, don't reset form
        } else {
          this.$notify({
            type: 'success',
            message: this.$t('seasonTickets.edit.successNotification', payload),
            icon: 'tim-icons icon-alert-circle-exc'
          })
          this.resetForm()
        }
      });
    },
    resetForm: function () {
      this.modal = false;
      this.alert = false;
      // resetting form
      this.formDetails = {
        internal_name: this.currentDetails ? this.currentDetails.internal_name : '',
        internal_description: this.currentDetails ? this.currentDetails.internal_description : '',
        discount_id: this.currentDetails ? this.currentDetails.discount_id : '',
        fare_group_id: this.currentDetails ? this.currentDetails.fare_group_id : '',
        duration: this.currentDetails ? this.currentDetails.duration : 1,
        trait: this.currentDetails ? this.currentDetails.trait : 'none',
      }
      // reset validation errors
      this.$validator.reset()

    },
  },
  mounted() {
    this.LoadDiscounts()
    this.LoadFareGroups()
  },
}
</script>
